import * as THREE from "three";
import type { TextureLibrary } from "../world/textures";
import type { MatLib } from "../world/materials";
import { QUALITY, type QualityPreset } from "../config";

type Particle = {
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  life: number;
  max: number;
  size: number;
  color: THREE.Color;
  kind: "spark" | "smoke" | "debris";
};

export class FX {
  group = new THREE.Group();
  private parts: Particle[] = [];
  private mesh: THREE.InstancedMesh;
  private dummy = new THREE.Object3D();
  private decals: THREE.Mesh[] = [];
  private tracers: { mesh: THREE.Mesh; life: number }[] = [];
  private decalMat: THREE.MeshBasicMaterial;
  private tracerGeo: THREE.CylinderGeometry;
  private tracerMat: THREE.MeshBasicMaterial;
  private cap: number;
  private allowDecals = true;
  private tmpC = new THREE.Color();

  constructor(scene: THREE.Scene, tex: TextureLibrary, _mats: MatLib, quality: QualityPreset) {
    this.cap = QUALITY[quality].particles;
    const geo = new THREE.PlaneGeometry(1, 1);
    const mat = new THREE.MeshBasicMaterial({
      map: tex.spark,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.InstancedMesh(geo, mat, this.cap);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(this.cap * 3), 3);
    this.mesh.frustumCulled = false;
    this.group.add(this.mesh);
    scene.add(this.group);

    this.decalMat = new THREE.MeshBasicMaterial({
      color: 0x1a120c,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
    });
    this.tracerGeo = new THREE.CylinderGeometry(0.012, 0.008, 1, 4);
    this.tracerGeo.rotateX(Math.PI / 2);
    this.tracerMat = new THREE.MeshBasicMaterial({
      color: 0xffc878,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
  }

  impact(point: THREE.Vector3, normal: THREE.Vector3, kind: "world" | "flesh") {
    const n = 8 + ((Math.random() * 6) | 0);
    for (let i = 0; i < n; i++) {
      const vel = normal
        .clone()
        .multiplyScalar(2 + Math.random() * 6)
        .add(new THREE.Vector3(Math.random() - 0.5, Math.random() * 0.8, Math.random() - 0.5).multiplyScalar(4));
      this.parts.push({
        pos: point.clone().addScaledVector(normal, 0.04),
        vel,
        life: kind === "flesh" ? 0.28 : 0.4,
        max: 0.4,
        size: kind === "flesh" ? 0.06 : 0.05 + Math.random() * 0.05,
        color: kind === "flesh" ? new THREE.Color(0x7a1510) : new THREE.Color(0xffb060),
        kind: kind === "flesh" ? "smoke" : "spark",
      });
    }
    this.parts.push({
      pos: point.clone().addScaledVector(normal, 0.05),
      vel: normal.clone().multiplyScalar(0.4),
      life: 0.55,
      max: 0.55,
      size: 0.22,
      color: new THREE.Color(kind === "flesh" ? 0x4a1210 : 0x889099),
      kind: "smoke",
    });
    if (this.allowDecals && kind === "world") {
      const d = new THREE.Mesh(new THREE.CircleGeometry(0.07 + Math.random() * 0.05, 8), this.decalMat.clone());
      d.position.copy(point).addScaledVector(normal, 0.02);
      d.lookAt(point.clone().add(normal));
      this.group.add(d);
      this.decals.push(d);
      if (this.decals.length > 80) {
        const old = this.decals.shift()!;
        this.group.remove(old);
        old.geometry.dispose();
      }
    }
    while (this.parts.length > this.cap) this.parts.shift();
  }

  tracer(from: THREE.Vector3, to: THREE.Vector3) {
    const len = from.distanceTo(to);
    const mesh = new THREE.Mesh(this.tracerGeo, this.tracerMat);
    mesh.position.copy(from).lerp(to, 0.5);
    mesh.lookAt(to);
    mesh.scale.set(1, 1, len);
    this.group.add(mesh);
    this.tracers.push({ mesh, life: 0.05 });
  }

  explosion(p: THREE.Vector3) {
    for (let i = 0; i < 28; i++) {
      const vel = new THREE.Vector3(Math.random() - 0.5, Math.random() * 0.8, Math.random() - 0.5)
        .normalize()
        .multiplyScalar(6 + Math.random() * 10);
      this.parts.push({
        pos: p.clone(),
        vel,
        life: 0.6 + Math.random() * 0.4,
        max: 0.8,
        size: 0.12 + Math.random() * 0.2,
        color: new THREE.Color().setHSL(0.07, 0.9, 0.55 + Math.random() * 0.2),
        kind: i % 3 === 0 ? "smoke" : "spark",
      });
    }
  }

  update(dt: number, camera: THREE.Camera) {
    for (let i = this.parts.length - 1; i >= 0; i--) {
      const p = this.parts[i];
      p.life -= dt;
      p.vel.y -= 6 * dt;
      p.pos.addScaledVector(p.vel, dt);
      p.vel.multiplyScalar(0.94);
      if (p.life <= 0) this.parts.splice(i, 1);
    }
    const q = camera.quaternion;
    for (let i = 0; i < this.cap; i++) {
      if (i < this.parts.length) {
        const p = this.parts[i];
        const t = 1 - p.life / p.max;
        this.dummy.position.copy(p.pos);
        this.dummy.quaternion.copy(q);
        this.dummy.scale.setScalar(p.size * (1 + t * 1.4));
        this.dummy.updateMatrix();
        this.mesh.setMatrixAt(i, this.dummy.matrix);
        this.mesh.setColorAt?.(i, p.color);
      } else {
        this.dummy.position.set(0, -99, 0);
        this.dummy.scale.setScalar(0.001);
        this.dummy.updateMatrix();
        this.mesh.setMatrixAt(i, this.dummy.matrix);
      }
    }
    this.mesh.instanceMatrix.needsUpdate = true;

    for (let i = this.tracers.length - 1; i >= 0; i--) {
      this.tracers[i].life -= dt;
      if (this.tracers[i].life <= 0) {
        this.group.remove(this.tracers[i].mesh);
        this.tracers.splice(i, 1);
      }
    }
  }
}
